import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CalculationData {
  // Kaufpreis & Nebenkosten
  purchasePrice?: string;
  agentCosts?: string;
  agentCostsPercentage?: string;
  acquisitionTax?: string;
  acquisitionTaxPercentage?: string;
  notaryCosts?: string;
  notaryCostsPercentage?: string;
  landRegistryCosts?: string;
  landRegistryCostsPercentage?: string;
  otherCosts?: string;
  
  // Grundsteuer
  propertyTaxRate?: string;
  propertyTax?: string;
  
  // Finanzierung
  eigenkapital?: string;
  creditTerm?: string;
  amortizationRate?: string;
  interestRate?: string;
  interestBindingTerm?: string;
  annualExtraRepayment?: string;
  monthlyRate?: string;
  changedInterestRate?: string;
  
  // Laufende Ausgaben
  adminCosts?: string;
  taxCosts?: string;
  houseMoney?: string;
  insurance?: string;
  repairCosts?: string;
  otherExpenses?: string;
  
  // Mieteinnahmen
  objectSize?: string;
  constructionYear?: number | null;
  rentalPrice?: string;
  monthlyRentalPrice?: string;
  yearlyRentalPrice?: string;
  additionalMonthlyIncome?: string;
  additionalYearlyIncome?: string;
  
  // Berechnete Gesamtwerte
  totalPurchaseCosts?: string;
  kreditbedarf?: string;
  totalYearlyIncome?: string;
  totalMonthlyExpenses?: string;
  totalYearlyExpenses?: string;
  
  // Erweiterte Berechnungen
  kaufpreisProQuadratmeter?: string;
  xfachVonKaufpreis?: string;
  xfachVonGesamtKaufkosten?: string;
  amortizationAmount?: string;
  interestAmount?: string;
  monthlyAmortization?: string;
  monthlyInterest?: string;
  totalAmortizationOverLifetime?: string;
  totalInterestOverLifetime?: string;
  totalPaymentOverLifetime?: string;
  totalCreditCostIncludingRemainingDebt?: string;
  totalInterestConstantRates?: string;
  totalPaymentOverLifetimeConstantRates?: string;
  totalInterestChangedRates?: string;
  totalPaymentOverLifetimeChangedRates?: string;
  totalInterestDuringBindingTerm?: string;
  remainingDebtAfterBindingTerm?: string;
  monthlyIncome?: string;
  monthlyFinancingExpenses?: string;
  monthlyOtherExpenses?: string;
  monthlyIncomeExcess?: string;
  yearlyIncomeExcess?: string;
  yearlyFinancingExpenses?: string;
  yearlyOtherExpenses?: string;
  calculatedCreditTerm?: string;
  calculatedCreditTermChangedRates?: string;
  
  // Szenarien Appreciation Rates
  scenario1AppreciationRate?: string;
  scenario2AppreciationRate?: string;
  scenario3AppreciationRate?: string;
  
  // Szenario 1 berechnete Werte
  scenario1Duration?: string;
  scenario1TotalCreditCost?: string;
  scenario1TotalRunningExpenses?: string;
  scenario1TotalIncome?: string;
  scenario1NetCashflow?: string;
  scenario1RemainingDebt?: string;
  scenario1PropertyValue?: string;
  scenario1TotalResult?: string;
  scenario1EquityPercentage?: string;
  
  // Szenario 2 berechnete Werte
  scenario2Duration?: string;
  scenario2TotalCreditCost?: string;
  scenario2TotalRunningExpenses?: string;
  scenario2TotalIncome?: string;
  scenario2NetCashflow?: string;
  scenario2RemainingDebt?: string;
  scenario2PropertyValue?: string;
  scenario2TotalResult?: string;
  scenario2EquityPercentage?: string;
  
  // Szenario 3 berechnete Werte
  scenario3Duration?: string;
  scenario3TotalCreditCost?: string;
  scenario3TotalRunningExpenses?: string;
  scenario3TotalIncome?: string;
  scenario3NetCashflow?: string;
  scenario3RemainingDebt?: string;
  scenario3PropertyValue?: string;
  scenario3TotalResult?: string;
  scenario3EquityPercentage?: string;
}

export interface SaveCalculationRequest {
  name: string;
  country?: string;
  city?: string;
  address?: string;
  postalCode?: string;
  calculationData: CalculationData;
}

export interface CalculationListItem {
  calculationId: number;
  name: string;
  city?: string;
  address?: string;
  createdAt: string;
  updatedAt: string;
  lastAccessedAt?: string;
}

export interface CalculationDto {
  calculationId: number;
  userId: number;
  name: string;
  country?: string;
  city?: string;
  address?: string;
  postalCode?: string;
  calculationData: CalculationData;
  createdAt: string;
  updatedAt: string;
  lastAccessedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CalculationStorageService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/calculations`;

  /**
   * Alle Berechnungen des aktuellen Users abrufen
   */
  getMyCalculations(): Observable<CalculationListItem[]> {
    return this.http.get<CalculationListItem[]>(this.baseUrl);
  }

  /**
   * Spezifische Berechnung abrufen
   */
  getCalculation(id: number): Observable<CalculationDto> {
    return this.http.get<CalculationDto>(`${this.baseUrl}/${id}`);
  }

  /**
   * Letzte zugegriffene Berechnung abrufen
   */
  getLatestCalculation(): Observable<CalculationDto | null> {
    return this.http.get<CalculationDto | null>(`${this.baseUrl}/latest`);
  }

  /**
   * Prüfen ob ein Name bereits existiert
   */
  checkNameExists(name: string, excludeId?: number): Observable<{ exists: boolean }> {
    let params = new HttpParams().set('name', name);
    if (excludeId !== undefined) {
      params = params.set('excludeId', excludeId.toString());
    }
    return this.http.get<{ exists: boolean }>(`${this.baseUrl}/check-name`, { params });
  }

  /**
   * Neue Berechnung speichern
   */
  saveCalculation(request: SaveCalculationRequest): Observable<CalculationDto> {
    return this.http.post<CalculationDto>(this.baseUrl, request);
  }

  /**
   * Berechnung aktualisieren
   */
  updateCalculation(id: number, request: SaveCalculationRequest): Observable<CalculationDto> {
    return this.http.put<CalculationDto>(`${this.baseUrl}/${id}`, request);
  }

  /**
   * Berechnung löschen
   */
  deleteCalculation(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/${id}`);
  }

  /**
   * Berechnung umbenennen
   */
  renameCalculation(id: number, newName: string): Observable<{ message: string; name: string }> {
    return this.http.patch<{ message: string; name: string }>(`${this.baseUrl}/${id}/rename`, { newName });
  }

  /**
   * Berechnung duplizieren
   */
  duplicateCalculation(id: number, newName: string): Observable<CalculationDto> {
    return this.http.post<CalculationDto>(`${this.baseUrl}/${id}/duplicate`, { newName });
  }

  /**
   * Generiere Namensvorschlag aus Stadt und Straße
   */
  generateNameSuggestion(city?: string, address?: string): string {
    if (city && address) {
      return `${city} - ${address}`;
    }
    if (city) {
      return city;
    }
    if (address) {
      return address;
    }
    return 'Neue Berechnung';
  }

  /**
   * Generiere eindeutigen Namensvorschlag mit Suffix für Duplikate
   */
  generateDuplicateNameSuggestion(existingNames: string[], baseName: string): string {
    // Wenn Basisname nicht existiert, verwende ihn
    if (!existingNames.includes(baseName)) {
      return baseName;
    }
    
    // Finde die nächste verfügbare Nummer
    let counter = 1;
    let newName = `${baseName} - ${counter}`;
    
    while (existingNames.includes(newName)) {
      counter++;
      newName = `${baseName} - ${counter}`;
    }
    
    return newName;
  }
}
